import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    User, Briefcase, GraduationCap, Award, FileText,
    MapPin, Mail, Phone, Globe, Calendar, Hash,
    ChevronRight, CheckCircle, Shield, X, Download,
    MessageSquare, ExternalLink, Send, Clock, Plus,
    FileDown, Trash2, Edit2, AlertCircle, Info, MoreVertical,
    ArrowLeft, UserCheck, Maximize2, Eye, EyeOff
} from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useLanguage } from '../context/LanguageContext';

import {
    EditPersonalDetailsModal,
    EditAddressModal,
    EditNationalityModal,
    EditLanguageModal,
    AddEmploymentModal,
    AddEducationModal,
    AddCertificateModal,
    AddDocumentModal,
    AddNoteModal,
    AddSkillModal
} from './profile';

const CandidateProfileView = ({ candidate: propCandidate, onClose, onAction, onSaved, isPageView = false, embedded = false, userId: userIdProp }) => {
    const { user: currentUser, impersonate } = useAuth();
    const { showToast } = useToast();
    const { t } = useLanguage();
    const navigate = useNavigate();
    const params = useParams();
    // Fallback: Extract userId from URL if not in params (e.g. when rendered under wildcard route)
    // Handle cases like /staff/profile/123 or /staff/profile/123/
    const getUserIdFromPath = () => {
        if (userIdProp) return userIdProp;
        if (params.userId) return params.userId;
        const path = window.location.pathname;
        const matches = path.match(/\/profile\/(\d+)/);
        if (matches && matches[1]) return matches[1];

        const segments = path.split('/').filter(Boolean);
        return segments.pop();
    };

    const userId = getUserIdFromPath();

    console.log('[CandidateProfileView] userId:', userId, 'params:', params, 'path:', window.location.pathname);
    const [candidate, setCandidate] = useState(propCandidate);
    const [loading, setLoading] = useState(!propCandidate);

    useEffect(() => {
        // Fetch full data if we have an ID but no candidate, OR if the candidate is a 'lite' version without documents
        const needsFullFetch = userId && (!propCandidate || (propCandidate && !propCandidate.documents));

        if (needsFullFetch) {
            fetchUserData();
        } else if (propCandidate) {
            setCandidate(propCandidate);
            setLoading(false);
        }
    }, [isPageView, userId, propCandidate]);

    const getPhotoUrl = (photoUrl) => {
        if (!photoUrl) return null;
        if (photoUrl.startsWith('data:')) return photoUrl;
        if (photoUrl.startsWith('http')) return photoUrl;

        let baseUrl = api.defaults.baseURL || '';
        if (!baseUrl) {
            const origin = window.location.origin;
            // Assumption: Local development uses port 8080 for backend
            baseUrl = origin.replace(':3000', ':8080');
        }

        // Clean up baseUrl
        if (baseUrl.endsWith('/api')) {
            baseUrl = baseUrl.slice(0, -4);
        } else if (baseUrl.includes('/api/')) {
            baseUrl = baseUrl.replace('/api/', '/');
        } else if (baseUrl.endsWith('/api/')) {
            baseUrl = baseUrl.slice(0, -5);
        }
        baseUrl = baseUrl.replace(/\/$/, '');

        const normalizedPhotoUrl = photoUrl.startsWith('/') ? photoUrl : `/${photoUrl}`;
        // Add timestamp to prevent caching issues
        return `${baseUrl}${normalizedPhotoUrl}?t=${new Date().getTime()}`;
    };

    const calculateAge = (dob) => {
        if (!dob) return null;
        const birthDate = new Date(dob);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    };

    const handleViewDocument = async (doc, index) => {
        if (!doc) return;

        if (typeof doc === 'string') {
            if (doc.startsWith('data:')) {
                try {
                    const parts = doc.split(';base64,');
                    if (parts.length === 2) {
                        const contentType = parts[0].split(':')[1];
                        const byteCharacters = atob(parts[1]);
                        const byteNumbers = new Array(byteCharacters.length);
                        for (let i = 0; i < byteCharacters.length; i++) {
                            byteNumbers[i] = byteCharacters.charCodeAt(i);
                        }
                        const byteArray = new Uint8Array(byteNumbers);
                        const blob = new Blob([byteArray], { type: contentType });
                        window.open(URL.createObjectURL(blob), '_blank');
                        return;
                    }
                } catch (e) {
                    console.error('Error viewing base64 document:', e);
                }
                const pdfWindow = window.open("");
                pdfWindow.document.write(`<iframe width='100%' height='100%' src='${doc}' style='border:0;'></iframe>`);
            } else {
                const url = (doc.startsWith('http://') || doc.startsWith('https://'))
                    ? doc
                    : getPhotoUrl(doc);
                window.open(url, '_blank');
            }
            return;
        }

        try {
            setLoading(true);
            const currentPath = window.location.pathname;
            let endpoint;

            if (currentPath.includes('/admin')) {
                endpoint = `/admin/users/${userId}/documents/${index}`;
            } else if (currentPath.includes('/staff')) {
                endpoint = `/staff/users/${userId}/documents/${index}`;
            } else if (currentPath.includes('/recruiting')) {
                endpoint = `/recruiting/candidates/${userId}/documents/${index}`;
            } else {
                endpoint = `/student/profile/documents/${index}`;
            }

            const response = await api.get(endpoint);
            if (response.data && response.data.data) {
                const fileData = response.data.data;
                if (fileData.startsWith('data:')) {
                    try {
                        const parts = fileData.split(';base64,');
                        if (parts.length === 2) {
                            const contentType = parts[0].split(':')[1];
                            const byteCharacters = atob(parts[1]);
                            const byteNumbers = new Array(byteCharacters.length);
                            for (let i = 0; i < byteCharacters.length; i++) {
                                byteNumbers[i] = byteCharacters.charCodeAt(i);
                            }
                            const byteArray = new Uint8Array(byteNumbers);
                            const blob = new Blob([byteArray], { type: contentType });
                            window.open(URL.createObjectURL(blob), '_blank');
                            return;
                        }
                    } catch (e) {
                        console.error('Error creating blob for document:', e);
                    }
                    const pdfWindow = window.open("");
                    pdfWindow.document.write(`<iframe width='100%' height='100%' src='${fileData}' style='border:0;'></iframe>`);
                } else {
                    window.open(fileData, '_blank');
                }
            } else {
                showToast('Document data could not be loaded.', 'error');
            }
        } catch (err) {
            console.error('Error downloading document:', err);
            showToast('Failed to load document content.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadDocument = async (doc, index) => {
        if (!doc) return;

        const downloadFile = (data, fileName) => {
            if (typeof data === 'string' && data.startsWith('data:')) {
                try {
                    const parts = data.split(';base64,');
                    if (parts.length === 2) {
                        const contentType = parts[0].split(':')[1] || 'application/octet-stream';
                        const byteCharacters = atob(parts[1]);
                        const byteNumbers = new Array(byteCharacters.length);
                        for (let i = 0; i < byteCharacters.length; i++) {
                            byteNumbers[i] = byteCharacters.charCodeAt(i);
                        }
                        const byteArray = new Uint8Array(byteNumbers);
                        const blob = new Blob([byteArray], { type: contentType });
                        const objectUrl = URL.createObjectURL(blob);

                        const link = document.createElement('a');
                        link.href = objectUrl;
                        link.download = fileName || 'document';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);

                        setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
                        return;
                    }
                } catch (e) {
                    console.error('Error creating blob for download:', e);
                }
            }

            // Fallback for non-data URIs
            const link = document.createElement('a');
            link.href = data;
            link.download = fileName || 'document';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        };

        if (typeof doc === 'string') {
            downloadFile(doc, 'document');
            return;
        }

        try {
            setLoading(true);
            const currentPath = window.location.pathname;
            let endpoint;

            if (currentPath.includes('/admin')) {
                endpoint = `/admin/users/${userId}/documents/${index}`;
            } else if (currentPath.includes('/staff')) {
                endpoint = `/staff/users/${userId}/documents/${index}`;
            } else if (currentPath.includes('/recruiting')) {
                endpoint = `/recruiting/candidates/${userId}/documents/${index}`;
            } else {
                endpoint = `/student/profile/documents/${index}`;
            }

            const response = await api.get(endpoint);
            if (response.data && response.data.data) {
                downloadFile(response.data.data, doc.name || `document_${index + 1}`);
            } else {
                showToast('Document data could not be loaded.', 'error');
            }
        } catch (err) {
            console.error('Error downloading document:', err);
            showToast('Failed to download document content.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchUserData = async () => {
        try {
            setLoading(true);
            const currentPath = window.location.pathname;
            let endpoint;

            if (currentPath.includes('/admin')) {
                endpoint = `/admin/users/${userId}`;
            } else if (currentPath.includes('/staff')) {
                endpoint = `/staff/users/${userId}`;
            } else if (currentPath.includes('/recruiting')) {
                endpoint = `/recruiting/candidates/${userId}`;
            } else {
                endpoint = `/admin/users/${userId}`; // fallback
            }

            const response = await api.get(endpoint);
            const data = response.data;

            // Ensure all JSON fields are parsed if they come as strings
            const jsonFields = ['skills', 'education_history', 'employment_history', 'certificates', 'documents', 'language_certificates', 'notes'];
            jsonFields.forEach(field => {
                if (data[field] && typeof data[field] === 'string') {
                    try {
                        const parsed = JSON.parse(data[field]);
                        if (Array.isArray(parsed)) {
                            data[field] = parsed;
                        } else if (field === 'skills' || field === 'language_certificates') {
                            data[field] = typeof parsed === 'string' ? parsed.split(',').map(s => s.trim()).filter(Boolean) : [];
                        } else {
                            data[field] = [];
                        }
                    } catch (e) {
                        console.warn(`Failed to parse ${field}:`, e);
                        data[field] = (field === 'skills' || field === 'language_certificates') ? data[field].split(',').map(s => s.trim()).filter(Boolean) : [];
                    }
                } else if (!data[field] || !Array.isArray(data[field])) {
                    data[field] = [];
                }
            });

            setCandidate(data);
        } catch (error) {
            console.error('Error fetching user data:', error);
            // Don't alert immediately, show error state in UI
        } finally {
            setLoading(false);
        }
    };
    const [activeTab, setActiveTab] = useState('profile');

    // Edit Modal States
    const [editingSection, setEditingSection] = useState(null); // 'personal', 'address', 'nationality', 'language'
    const [editingLanguageIndex, setEditingLanguageIndex] = useState(null);
    const [showEmploymentModal, setShowEmploymentModal] = useState(false);
    const [editingEmploymentIndex, setEditingEmploymentIndex] = useState(null);
    const [showEducationModal, setShowEducationModal] = useState(false);
    const [editingEducationIndex, setEditingEducationIndex] = useState(null);
    const [showCertificateModal, setShowCertificateModal] = useState(false);
    const [editingCertificateIndex, setEditingCertificateIndex] = useState(null);
    const [showDocumentModal, setShowDocumentModal] = useState(false);
    const [editingDocumentIndex, setEditingDocumentIndex] = useState(null);
    const [showNoteModal, setShowNoteModal] = useState(false);
    const [editingNoteIndex, setEditingNoteIndex] = useState(null);
    const [showSkillModal, setShowSkillModal] = useState(false);
    const [showPhotoExpansion, setShowPhotoExpansion] = useState(false);
    const [importantUpdate, setImportantUpdate] = useState('');
    const [isPostingUpdate, setIsPostingUpdate] = useState(false);

    const handlePostUpdate = async () => {
        if (!importantUpdate.trim()) return;
        try {
            setIsPostingUpdate(true);
            await api.post('/staff/important-update', {
                user_id: parseInt(userId),
                message: importantUpdate.trim()
            });
            showToast('Update posted successfully!', 'success');
            setImportantUpdate('');
        } catch (error) {
            console.error('Error posting update:', error);
            showToast('Failed to post update', 'error');
        } finally {
            setIsPostingUpdate(false);
        }
    };

    const isPartnerViewer = currentUser?.role === 'sourcing_partner' || currentUser?.role === 'recruiting_partner';
    const isSourcingPartner = currentUser?.role === 'sourcing_partner';

    const tabs = [
        { id: 'profile', label: 'Profile' },
        ...(!isSourcingPartner ? [
            { id: 'employment', label: 'Employment & Qualification' },
            { id: 'skills', label: 'Skills & Certificates' }
        ] : []),
        { id: 'documents', label: 'Documents' },
        { id: 'notes', label: 'Notes' }
    ];

    // Helper to update candidate state and backend
    const updateCandidateProfile = async (updateData) => {
        try {
            const isFormData = updateData instanceof FormData;
            const currentPath = window.location.pathname;
            let endpoint;

            if (currentPath.includes('/admin/profile/') || currentPath.includes('/admin/users/')) {
                endpoint = `/admin/users/${candidate.id}`;
            } else {
                endpoint = `/staff/users/${candidate.id}`;
            }

            // If FormData, we cannot easily do optimistic updates for file uploads
            // So we skip optimistic update for FormData or if it's complex
            if (!isFormData) {
                // Before setting candidate state, we need to ensure JSON strings are parsed 
                // for the local state, otherwise .map() calls in the UI will crash.
                const stateUpdate = { ...updateData };
                const jsonFields = ['skills', 'education_history', 'employment_history', 'certificates', 'documents', 'language_certificates', 'notes'];

                jsonFields.forEach(field => {
                    if (stateUpdate[field] && typeof stateUpdate[field] === 'string') {
                        try {
                            stateUpdate[field] = JSON.parse(stateUpdate[field]);
                        } catch (e) {
                            console.warn(`Failed to parse ${field} for optimistic update:`, e);
                        }
                    }
                });

                const updatedCandidate = { ...candidate, ...stateUpdate };
                setCandidate(updatedCandidate);
            }

            // API call
            const config = isFormData ? { headers: { 'Content-Type': undefined } } : {};
            await api.put(endpoint, updateData, config);

            // For FormData or to ensure consistency, refetch data
            // This is safer than relying on local state merge for complex updates
            await fetchUserData();

            if (onSaved) onSaved(candidate); // Pass current, or wait for fetch
            return true;
        } catch (error) {
            console.error('Error updating profile:', error);
            showToast('Failed to update profile', 'error');
            // Revert optimistic update if needed (fetching data does this)
            fetchUserData();
            return false;
        }
    };

    const handleImpersonate = async (userId, role) => {
        try {
            const success = await impersonate(userId);
            if (success) {
                showToast('Impersonation successful!', 'success');
                // Role-based redirection
                if (role === 'candidate') navigate('/student');
                else if (role === 'recruiting_partner') navigate('/recruiting');
                else if (role === 'sourcing_partner') navigate('/sourcing');
                else if (role === 'teacher') navigate('/teacher');
                else navigate('/');
            }
        } catch (error) {
            console.error('[CandidateProfileView] Impersonation failed:', error);
            showToast('Impersonation failed. Please try again.', 'error');
        }
    };

    const handleSectionSave = async (data) => {
        // data contains the fields updated by the modal (e.g., gender, dob, address fields)
        await updateCandidateProfile(data);
        setEditingSection(null);
    };

    const handleLanguageSave = async (languageData) => {
        const currentLangs = candidate.language_certificates || [];
        let updatedLangs;

        if (editingLanguageIndex !== null) {
            updatedLangs = [...currentLangs];
            updatedLangs[editingLanguageIndex] = languageData;
        } else {
            updatedLangs = [...currentLangs, languageData];
        }

        await updateCandidateProfile({ language_certificates: JSON.stringify(updatedLangs) });
        setEditingSection(null);
        setEditingLanguageIndex(null);
    };

    const handleEmploymentSave = async (employmentData) => {
        const currentEmployment = candidate.employment_history || [];
        let updatedEmployment;

        if (editingEmploymentIndex !== null) {
            updatedEmployment = [...currentEmployment];
            updatedEmployment[editingEmploymentIndex] = employmentData;
        } else {
            updatedEmployment = [...currentEmployment, employmentData];
        }

        await updateCandidateProfile({ employment_history: JSON.stringify(updatedEmployment) });
        setShowEmploymentModal(false);
        setEditingEmploymentIndex(null);
    };

    const handleEducationSave = async (educationData) => {
        const currentEducation = candidate.education_history || [];
        let updatedEducation;

        if (editingEducationIndex !== null) {
            updatedEducation = [...currentEducation];
            updatedEducation[editingEducationIndex] = educationData;
        } else {
            updatedEducation = [...currentEducation, educationData];
        }

        await updateCandidateProfile({ education_history: JSON.stringify(updatedEducation) });
        setShowEducationModal(false);
        setEditingEducationIndex(null);
    };

    const handleCertificateSave = async (certificateData) => {
        const currentCerts = candidate.certificates || [];
        let updatedCerts;

        if (editingCertificateIndex !== null) {
            updatedCerts = [...currentCerts];
            updatedCerts[editingCertificateIndex] = certificateData;
        } else {
            updatedCerts = [...currentCerts, certificateData];
        }

        await updateCandidateProfile({ certificates: JSON.stringify(updatedCerts) });
        setShowCertificateModal(false);
        setEditingCertificateIndex(null);
    };

    const handleDocumentSave = async (documentEntry) => {
        try {
            const currentDocs = candidate.documents || [];
            let updatedDocs;

            if (editingDocumentIndex !== null) {
                updatedDocs = [...currentDocs];
                updatedDocs[editingDocumentIndex] = documentEntry;
            } else {
                updatedDocs = [...currentDocs, documentEntry];
            }

            const formData = new FormData();

            // Strip file objects before serializing - files are sent separately
            const cleanDocs = updatedDocs.map(d => {
                const { file, ...rest } = d;
                return rest;
            });
            formData.append('documents', JSON.stringify(cleanDocs));

            // Handle file upload if present
            if (documentEntry.file) {
                formData.append('document_file', documentEntry.file);
                // Note: backend expects 'document_file' to be handled when 'documents' JSON is present
            }

            await updateCandidateProfile(formData);
            setShowDocumentModal(false);
            setEditingDocumentIndex(null);
        } catch (error) {
            console.error('Error saving document:', error);
            showToast('Failed to save document. Please try again.', 'error');
        }
    };

    const handleDeleteDocument = async (index) => {
        if (!window.confirm('Are you sure you want to delete this document?')) return;
        try {
            const currentDocs = candidate?.documents || [];
            const updatedDocs = currentDocs.filter((_, i) => i !== index);

            const formData = new FormData();
            formData.append('documents', JSON.stringify(updatedDocs));

            await updateCandidateProfile(formData);
        } catch (error) {
            console.error('Error deleting document:', error);
            showToast('Failed to delete document', 'error');
        }
    };

    const handleNoteSave = async (noteData) => {
        const currentNotes = candidate.notes || [];
        let updatedNotes;

        // Ensure note has author if new
        if (!noteData.author) {
            noteData.author = currentUser?.name || 'Staff';
            noteData.created_at = new Date().toISOString();
        }

        if (editingNoteIndex !== null) {
            updatedNotes = [...currentNotes];
            updatedNotes[editingNoteIndex] = noteData;
        } else {
            updatedNotes = [...currentNotes, noteData];
        }

        await updateCandidateProfile({ notes: JSON.stringify(updatedNotes) });
        setShowNoteModal(false);
        setEditingNoteIndex(null);
    };

    const handleToggleDocumentVisibility = async (index) => {
        const currentDocs = [...(candidate.documents || [])];
        const doc = currentDocs[index];
        // Default is visible if undefined
        const currentVisibility = doc.is_visible_to_recruiter !== false;

        doc.is_visible_to_recruiter = !currentVisibility;
        currentDocs[index] = doc;

        await updateCandidateProfile({ documents: JSON.stringify(currentDocs) });
    };

    const handleAddSkill = async (skillData) => {
        // skillData is { name: '...', proficiency: '...' }
        // Backend expects 'skills' as array of objects or strings.
        // Let's standardize on objects.
        const currentSkills = candidate.skills || [];
        const updatedSkills = [...currentSkills, skillData];

        await updateCandidateProfile({ skills: JSON.stringify(updatedSkills) });
        setShowSkillModal(false);
    };

    const handleDeleteSkill = async (skillToDelete) => {
        const currentSkills = candidate.skills || [];
        const updatedSkills = currentSkills.filter(s => {
            const sName = typeof s === 'string' ? s : s.name;
            const deleteName = typeof skillToDelete === 'string' ? skillToDelete : skillToDelete.name;
            return sName !== deleteName;
        });

        await updateCandidateProfile({ skills: JSON.stringify(updatedSkills) });
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case 'profile':
                // Partner-specific layout
                if (candidate?.role === 'recruiting_partner' || candidate?.role === 'sourcing_partner') {
                    const InfoRow = ({ label, value }) => (
                        <div className="flex flex-col gap-0.5">
                            <p className="text-xs text-slate-500">{label}</p>
                            <p className="text-sm font-semibold text-black">{value || '-'}</p>
                        </div>
                    );
                    const DocLink = ({ label, url }) => url ? (
                        <a href={url.startsWith('http') ? url : `http://localhost:8080${url}`}
                            target="_blank" rel="noreferrer"
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-cyan-600 bg-cyan-50 hover:bg-cyan-100 rounded-lg border border-cyan-200 transition-all">
                            <ExternalLink className="h-3 w-3" /> {label}
                        </a>
                    ) : (
                        <span className="text-xs text-slate-400 italic">{label} – not uploaded</span>
                    );
                    return (
                        <div className="space-y-6 animate-fadeIn">
                            {/* 1. Company Information */}
                            <div className="bg-white rounded-xl border border-slate-200 p-5">
                                <h3 className="text-base font-bold text-black mb-4">1. Company Information</h3>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
                                    <InfoRow label="Legal Entity Name" value={candidate?.legal_entity_name} />
                                    <InfoRow label="Trade Name / Brand" value={candidate?.trade_name} />
                                    <InfoRow label="Company Website" value={candidate?.company_website} />
                                    <InfoRow label="Year of Establishment" value={candidate?.year_of_establishment} />
                                    <InfoRow label="Company Size" value={candidate?.company_size} />
                                    <InfoRow label="Specialization" value={candidate?.specialization} />
                                    {candidate?.company_website && (
                                        <div className="flex flex-col gap-0.5">
                                            <p className="text-xs text-slate-500">Website Link</p>
                                            <a href={candidate.company_website} target="_blank" rel="noreferrer"
                                                className="text-sm font-semibold text-cyan-600 hover:underline truncate">
                                                {candidate.company_website}
                                            </a>
                                        </div>
                                    )}
                                    {candidate?.logo_url && (
                                        <div className="flex flex-col gap-1.5 md:col-span-3">
                                            <p className="text-xs text-slate-500">Firm Logo</p>
                                            <img
                                                src={candidate.logo_url.startsWith('http') ? candidate.logo_url : `http://localhost:8080${candidate.logo_url}`}
                                                alt="Firm Logo"
                                                className="h-14 w-auto object-contain rounded-md border border-slate-200 bg-slate-50 p-1"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* 2. Point of Contact */}
                            <div className="bg-white rounded-xl border border-slate-200 p-5">
                                <h3 className="text-base font-bold text-black mb-4">2. Point of Contact</h3>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
                                    <InfoRow label="Account Manager" value={candidate?.account_manager_name} />
                                    <InfoRow label="Official Email" value={candidate?.official_email} />
                                    <InfoRow label="Direct Phone" value={candidate?.direct_phone} />
                                    <InfoRow label="Escalation Contact" value={candidate?.escalation_contact_name} />
                                    <InfoRow label="Escalation Email" value={candidate?.escalation_contact_email} />
                                </div>
                            </div>

                            {/* 3. Statutory & Compliance */}
                            <div className="bg-white rounded-xl border border-slate-200 p-5">
                                <h3 className="text-base font-bold text-black mb-4">3. Statutory &amp; Compliance</h3>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-5 mb-5">
                                    <InfoRow label="Tax ID (PAN/GSTIN/TIN)" value={candidate?.tax_id} />
                                </div>
                                <div className="flex flex-wrap gap-3">
                                    <DocLink label="Incorporation Certificate" url={candidate?.incorporation_certificate_url} />
                                    <DocLink label="Labor License" url={candidate?.labor_license_url} />
                                    <DocLink label="MSME / Diversity Cert." url={candidate?.msme_diversity_certificate_url} />
                                </div>
                            </div>
                        </div>
                    );
                }

                // Default candidate layout
                return (
                    <div className="space-y-6 animate-fadeIn">
                        {/* Personal Details */}
                        <div className="bg-white rounded-xl border border-slate-200 p-5">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-base font-bold text-black">Personal Details</h3>
                                {!isPartnerViewer && <button
                                    onClick={() => setEditingSection('personal')}
                                    className="p-2 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-all"
                                >
                                    <Edit2 className="h-4 w-4" />
                                </button>}
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                                <div className="flex items-start gap-2">
                                    <User className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                                    <div>
                                        <p className="text-xs text-slate-500">Gender</p>
                                        <p className="text-sm font-medium text-black">{candidate?.gender || '-'}</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-2">
                                    <Calendar className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                                    <div>
                                        <p className="text-xs text-slate-500">Date Of Birth</p>
                                        <p className="text-sm font-medium text-black">
                                            {candidate?.date_of_birth ? new Date(candidate.date_of_birth).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-2">
                                    <Clock className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                                    <div>
                                        <p className="text-xs text-slate-500">{t('Age', 'Age')}</p>
                                        <p className="text-sm font-medium text-black">
                                            {candidate?.date_of_birth ? `${calculateAge(candidate.date_of_birth)} ${t('years', 'years')}` : '-'}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-2">
                                    <FileText className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                                    <div>
                                        <p className="text-xs text-slate-500">Candidate Ref. No</p>
                                        <p className="text-sm font-medium text-black">{candidate?.candidate_ref_number || '-'}</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-2">
                                    <Globe className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                                    <div>
                                        <p className="text-xs text-slate-500">Native Language</p>
                                        <p className="text-sm font-medium text-black">{candidate?.native_language || '-'}</p>
                                    </div>
                                </div>
                            </div>
                            <p className="text-sm text-slate-600 leading-relaxed">
                                {candidate?.about_me || candidate?.bio || 'No bio added yet.'}
                            </p>
                        </div>

                        {/* Address Section */}
                        <div className="bg-white rounded-xl border border-slate-200 p-5">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="text-base font-bold text-black">Address</h3>
                                {!isPartnerViewer && <button
                                    onClick={() => setEditingSection('address')}
                                    className="p-2 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-all"
                                >
                                    <Edit2 className="h-4 w-4" />
                                </button>}
                            </div>
                            <div className="flex items-start gap-2">
                                <MapPin className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                                <p className="text-sm text-slate-600">
                                    {[candidate?.house_number, candidate?.street_name, candidate?.city, candidate?.state, candidate?.country, candidate?.postal_code]
                                        .filter(Boolean).join(', ') || candidate?.address || 'No address added yet.'}
                                </p>
                            </div>
                        </div>

                        {/* Nationality Section */}
                        <div className="bg-white rounded-xl border border-slate-200 p-5">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="text-base font-bold text-black">Nationality</h3>
                                {!isPartnerViewer && <button
                                    onClick={() => setEditingSection('nationality')}
                                    className="p-2 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-all"
                                >
                                    <Edit2 className="h-4 w-4" />
                                </button>}
                            </div>
                            <div className="flex items-center gap-4">
                                <span className="text-sm font-medium text-black">{candidate?.nationality || 'India'}</span>
                                {candidate?.identification_number && (
                                    <span className="text-sm text-slate-600">{candidate.identification_number}</span>
                                )}
                            </div>
                        </div>

                        {/* Language Section */}
                        <div className="bg-white rounded-xl border border-slate-200 p-5">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-base font-bold text-black">Language</h3>
                                {!isPartnerViewer && <button
                                    onClick={() => {
                                        setEditingLanguageIndex(null);
                                        setEditingSection('language');
                                    }}
                                    className="p-2 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-all"
                                >
                                    <Plus className="h-4 w-4" />
                                </button>}
                            </div>
                            {(() => {
                                const source = candidate?.language_certificates || candidate?.language_certs || [];
                                const langs = Array.isArray(source) ? source : [];

                                if (langs.length === 0) {
                                    return <p className="text-sm text-slate-400 text-center py-4">No language certificates added yet. Click + to add one.</p>;
                                }

                                return (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
                                                    <th className="py-2 font-medium">Language</th>
                                                    <th className="py-2 font-medium">Listening</th>
                                                    <th className="py-2 font-medium">Reading</th>
                                                    <th className="py-2 font-medium">Speaking</th>
                                                    <th className="py-2 font-medium">Writing</th>
                                                    <th className="py-2 font-medium w-10"></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {langs.map((lang, i) => (
                                                    <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50">
                                                        <td className="py-3 font-medium text-black">{lang.language}</td>
                                                        <td className="py-3 text-cyan-600 font-semibold">{lang.listening || lang.listening_level || lang.level || '-'}</td>
                                                        <td className="py-3 text-cyan-600 font-semibold">{lang.reading || lang.reading_level || lang.level || '-'}</td>
                                                        <td className="py-3 text-cyan-600 font-semibold">{lang.speaking || lang.speaking_level || lang.level || '-'}</td>
                                                        <td className="py-3 text-cyan-600 font-semibold">{lang.writing || lang.writing_level || lang.level || '-'}</td>
                                                        <td className="py-3">
                                                            <button
                                                                onClick={() => {
                                                                    setEditingLanguageIndex(i);
                                                                    setEditingSection('language');
                                                                }}
                                                                className="p-1.5 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded transition-all"
                                                            >
                                                                <Edit2 className="h-3.5 w-3.5" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                );
            case 'employment':
                return (
                    <div className="space-y-6">
                        {/* Employment History */}
                        <div className="bg-white rounded-xl border border-slate-200 p-5">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-base font-bold text-black">Employment</h3>
                                {!isPartnerViewer && <button
                                    onClick={() => {
                                        setEditingEmploymentIndex(null);
                                        setShowEmploymentModal(true);
                                    }}
                                    className="p-2 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-all"
                                >
                                    <Plus className="h-4 w-4" />
                                </button>}
                            </div>
                            {(() => {
                                const employment = candidate?.employment_history || [];
                                const jobs = Array.isArray(employment) ? employment : [];

                                return jobs.length > 0 ? (
                                    <div className="space-y-3">
                                        {jobs.map((job, i) => (
                                            <div key={i} className="p-4 bg-slate-50 rounded-lg border border-slate-100 flex items-start justify-between">
                                                <div className="flex items-start gap-3">
                                                    <div className="p-2 bg-cyan-50 rounded-lg">
                                                        <Briefcase className="h-5 w-5 text-cyan-600" />
                                                    </div>
                                                    <div>
                                                        <p className="font-semibold text-black">{job.title}</p>
                                                        <p className="text-sm text-slate-600">{job.company_name || job.company}</p>
                                                        <p className="text-xs text-slate-400 mt-1">
                                                            {job.start_date} - {job.no_end_date || job.is_current ? 'Present' : job.end_date}
                                                        </p>
                                                    </div>
                                                </div>
                                                {!isPartnerViewer && <button
                                                    onClick={() => {
                                                        setEditingEmploymentIndex(i);
                                                        setShowEmploymentModal(true);
                                                    }}
                                                    className="p-1.5 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded transition-all"
                                                >
                                                    <Edit2 className="h-4 w-4" />
                                                </button>}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-sm text-slate-400 text-center py-4">No employment history added yet. Click + to add one.</p>
                                );
                            })()}
                        </div>

                        {/* Education History */}
                        <div className="bg-white rounded-xl border border-slate-200 p-5">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-base font-bold text-black">Education</h3>
                                {!isPartnerViewer && <button
                                    onClick={() => {
                                        setEditingEducationIndex(null);
                                        setShowEducationModal(true);
                                    }}
                                    className="p-2 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-all"
                                >
                                    <Plus className="h-4 w-4" />
                                </button>}
                            </div>
                            {(() => {
                                const education = candidate?.education_history || [];
                                const edus = Array.isArray(education) ? education : [];

                                return edus.length > 0 ? (
                                    <div className="space-y-3">
                                        {edus.map((edu, i) => (
                                            <div key={i} className="p-4 bg-slate-50 rounded-lg border border-slate-100 flex items-start justify-between">
                                                <div className="flex items-start gap-3">
                                                    <div className="p-2 bg-purple-50 rounded-lg">
                                                        <GraduationCap className="h-5 w-5 text-purple-600" />
                                                    </div>
                                                    <div>
                                                        <p className="font-semibold text-black">{edu.course_title || edu.qualification || edu.degree}</p>
                                                        <p className="text-sm text-slate-600">{edu.institution_name || edu.university || edu.institution}</p>
                                                        <p className="text-xs text-slate-400 mt-1">
                                                            {edu.course_level || edu.degree_type || edu.field_of_study} • {edu.start_date} - {edu.end_date || edu.year_of_passout}
                                                        </p>
                                                    </div>
                                                </div>
                                                {!isPartnerViewer && <button
                                                    onClick={() => {
                                                        setEditingEducationIndex(i);
                                                        setShowEducationModal(true);
                                                    }}
                                                    className="p-1.5 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded transition-all"
                                                >
                                                    <Edit2 className="h-4 w-4" />
                                                </button>}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-sm text-slate-400 text-center py-4">No education history added yet. Click + to add one.</p>
                                );
                            })()}
                        </div>
                    </div>
                );
            case 'skills':
                return (
                    <div className="space-y-6">
                        {/* Skills Section */}
                        <div className="bg-white rounded-xl border border-slate-200 p-5">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-base font-bold text-black">Professional Skills</h3>
                                {!isPartnerViewer && <button
                                    onClick={() => setShowSkillModal(true)}
                                    className="p-2 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-all"
                                >
                                    <Plus className="h-4 w-4" />
                                </button>}
                            </div>

                            {candidate?.skills && candidate.skills.length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                    {candidate.skills.map((skill, index) => (
                                        <div key={index} className="group relative px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-700 font-medium text-sm flex items-center gap-2 hover:border-cyan-300 hover:shadow-sm transition-all">
                                            <div className="flex flex-col">
                                                <span className="font-bold">{typeof skill === 'object' ? skill.name : skill}</span>
                                                {typeof skill === 'object' && (
                                                    <div className="flex items-center gap-1.5 mt-0.5">
                                                        <span className="text-[10px] text-cyan-600 font-black uppercase tracking-widest">{skill.proficiency}</span>
                                                        {skill.years_of_experience && (
                                                            <>
                                                                <span className="text-slate-300">•</span>
                                                                <span className="text-[10px] text-slate-400 font-bold">{skill.years_of_experience} Years Exp.</span>
                                                            </>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            {!isPartnerViewer && <button
                                                onClick={() => handleDeleteSkill(skill)}
                                                className="ml-2 p-1 text-slate-300 hover:text-red-500 rounded-full hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all"
                                                title="Remove skill"
                                            >
                                                <X className="h-3.5 w-3.5" />
                                            </button>}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm text-slate-400 text-center py-4">No skills added yet. Click + to add one.</p>
                            )}
                        </div>

                        {/* Certificates Section */}
                        <div className="bg-white rounded-xl border border-slate-200 p-5">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-base font-bold text-black">Licenses & Certificates</h3>
                                {!isPartnerViewer && <button
                                    onClick={() => {
                                        setEditingCertificateIndex(null);
                                        setShowCertificateModal(true);
                                    }}
                                    className="p-2 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-all"
                                >
                                    <Plus className="h-4 w-4" />
                                </button>}
                            </div>
                            {(() => {
                                const certs = candidate?.certificates || [];
                                const certList = Array.isArray(certs) ? certs : [];

                                return certList.length > 0 ? (
                                    <div className="space-y-3">
                                        {certList.map((cert, i) => (
                                            <div key={i} className="p-4 bg-slate-50 rounded-lg border border-slate-100 flex items-start justify-between">
                                                <div className="flex items-start gap-3">
                                                    <div className="p-2 bg-amber-50 rounded-lg">
                                                        <Award className="h-5 w-5 text-amber-600" />
                                                    </div>
                                                    <div>
                                                        <p className="font-semibold text-black">{cert.license_title || cert.title || cert.name}</p>
                                                        <p className="text-sm text-slate-600">Issued by: {cert.issued_by || cert.issuing_organization}</p>
                                                        <p className="text-xs text-slate-400 mt-1">
                                                            {cert.issuance_date || cert.issue_date} - {cert.no_end_date || cert.no_expiry ? 'No Expiry' : cert.expiry_date || cert.expiration_date}
                                                        </p>
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={() => {
                                                        setEditingCertificateIndex(i);
                                                        setShowCertificateModal(true);
                                                    }}
                                                    className="p-1.5 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded transition-all"
                                                >
                                                    <Edit2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-sm text-slate-400 text-center py-4">No licenses or certificates added yet. Click + to add one.</p>
                                );
                            })()}
                        </div>
                    </div>
                );
            case 'documents':
                return (
                    <div className="bg-white rounded-xl border border-slate-200 p-5">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-base font-bold text-black">Documents</h3>
                            <button
                                onClick={() => {
                                    setEditingDocumentIndex(null);
                                    setShowDocumentModal(true);
                                }}
                                className="p-2 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-all"
                            >
                                <Plus className="h-4 w-4" />
                            </button>
                        </div>
                        <div className="space-y-3">
                            {candidate?.resume_url && (
                                <div className="flex items-center justify-between p-4 bg-cyan-50/50 rounded-lg border border-cyan-100 mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className="h-10 w-10 bg-cyan-100 rounded-lg flex items-center justify-center text-cyan-600">
                                            <FileText className="h-5 w-5" />
                                        </div>
                                        <div>
                                            <h4 className="font-medium text-black">Main Resume</h4>
                                            <p className="text-xs text-slate-500">PDF / DOCX</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handleViewDocument(candidate.resume_url)}
                                            className="p-2 text-slate-400 hover:text-cyan-600 transition-colors"
                                            title="View Resume"
                                        >
                                            <ExternalLink className="h-4 w-4" />
                                        </button>
                                        <a href={candidate.resume_url} download className="p-2 text-slate-400 hover:text-cyan-600 transition-colors">
                                            <Download className="h-4 w-4" />
                                        </a>
                                    </div>
                                </div>
                            )}

                            {(() => {
                                const docs = candidate?.documents || [];
                                const docList = Array.isArray(docs) ? docs : [];

                                return docList.length > 0 ? (
                                    docList.map((doc, index) => (
                                        <div key={index} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                                            <div className="flex items-center gap-3">
                                                <div className="h-10 w-10 bg-cyan-50 rounded-lg flex items-center justify-center text-cyan-600">
                                                    <FileText className="h-5 w-5" />
                                                </div>
                                                <div>
                                                    <h4 className="font-medium text-black">
                                                        {doc.name || `Document ${index + 1}`}
                                                        {doc.is_visible_to_recruiter === false && (
                                                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                                                Hidden from Recruiters
                                                            </span>
                                                        )}
                                                    </h4>
                                                    <p className="text-xs text-slate-500">{doc.type || 'FILE'}</p>
                                                </div>
                                            </div>
                                            <div className="flex gap-2">
                                                {!isPartnerViewer && !isSourcingPartner && (
                                                    <button
                                                        onClick={() => handleToggleDocumentVisibility(index)}
                                                        className={`p-2 rounded-lg transition-colors ${doc.is_visible_to_recruiter === false ? 'text-red-500 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'}`}
                                                        title={doc.is_visible_to_recruiter === false ? "Hidden from recruiters. Click to make visible." : "Visible to recruiters. Click to hide."}
                                                    >
                                                        {doc.is_visible_to_recruiter === false ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                                    </button>
                                                )}
                                                {/* Since we don't have the data URL directly anymore, download will just view it for now, or we'd need another endpoint to trigger a download explicitly */}
                                                <button
                                                    onClick={() => handleViewDocument(doc, doc.original_index ?? index)}
                                                    className="p-2 text-slate-400 hover:text-cyan-600 transition-colors"
                                                    title="View Document"
                                                >
                                                    <ExternalLink className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDownloadDocument(doc, doc.original_index ?? index)}
                                                    className="p-2 text-slate-400 hover:text-cyan-600 transition-colors"
                                                    title="Download Document"
                                                >
                                                    <Download className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setEditingDocumentIndex(index);
                                                        setShowDocumentModal(true);
                                                    }}
                                                    className="p-2 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-colors"
                                                    title="Edit Document"
                                                >
                                                    <Edit2 className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteDocument(index)}
                                                    className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                    title="Delete Document"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    !candidate?.resume_url && <p className="text-slate-500 italic">No documents available.</p>
                                );
                            })()}
                        </div>
                    </div>
                );
            case 'notes':
                return (
                    <div className="bg-white rounded-xl border border-slate-200 p-6">
                        <div className="flex items-center justify-between border-b border-slate-100 pb-4 mb-4">
                            <h3 className="text-lg font-bold text-black">Notes</h3>
                            <button
                                onClick={() => {
                                    setEditingNoteIndex(null);
                                    setShowNoteModal(true);
                                }}
                                className="flex items-center gap-2 px-3 py-1.5 bg-cyan-500 text-white text-sm font-medium rounded-lg hover:bg-cyan-600 transition-colors"
                            >
                                <Plus className="h-4 w-4" /> Add Note
                            </button>
                        </div>

                        <div className="space-y-4">
                            {candidate?.notes && candidate.notes.length > 0 ? (
                                candidate.notes.map((note, index) => (
                                    <div key={index} className="p-4 bg-yellow-50 rounded-lg border border-yellow-100 relative group">
                                        <div className="flex justify-between items-start mb-2">
                                            <div className="flex items-center gap-2">
                                                <span className="h-6 w-6 bg-yellow-100 rounded-full flex items-center justify-center text-yellow-600 text-xs font-bold">
                                                    {note.author?.charAt(0) || 'S'}
                                                </span>
                                                <span className="text-xs font-semibold text-slate-700">
                                                    {note.author || 'Staff Member'}
                                                </span>
                                            </div>
                                            <span className="text-xs text-slate-400">
                                                {new Date(note.created_at).toLocaleDateString()}
                                            </span>
                                        </div>
                                        <p className="text-slate-700 text-sm whitespace-pre-wrap">{note.content || note.text || note.note}</p>
                                        <button
                                            onClick={() => {
                                                setEditingNoteIndex(index);
                                                setShowNoteModal(true);
                                            }}
                                            className="absolute top-2 right-2 p-1.5 text-slate-400 hover:text-cyan-600 bg-white/50 hover:bg-white rounded opacity-0 group-hover:opacity-100 transition-all"
                                        >
                                            <Edit2 className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                ))
                            ) : (
                                <div className="text-center py-8">
                                    <div className="h-12 w-12 bg-slate-100 rounded-full flex items-center justify-center text-slate-400 mx-auto mb-3">
                                        <MessageSquare className="h-6 w-6" />
                                    </div>
                                    <p className="text-slate-500">No notes added yet.</p>
                                    <button
                                        onClick={() => setShowNoteModal(true)}
                                        className="text-cyan-600 font-medium text-sm mt-1 hover:underline"
                                    >
                                        Add your first note
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="animate-fadeIn">
            {loading ? (
                <div className={embedded ? "flex items-center justify-center py-20" : "min-h-screen bg-slate-50 flex items-center justify-center"}>
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto mb-4"></div>
                        <p className="text-slate-600">Loading candidate data...</p>
                        {embedded && <p className="text-xs text-slate-400 mt-2">ID: {userId}</p>}
                    </div>
                </div>
            ) : !candidate ? (
                <div className={embedded ? "flex items-center justify-center py-20" : "min-h-screen bg-slate-50 flex items-center justify-center"}>
                    <div className="text-center">
                        <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <AlertCircle className="h-8 w-8 text-red-600" />
                        </div>
                        <h3 className="text-lg font-bold text-black">Failed to load profile</h3>
                        <p className="text-slate-600 mb-6">Could not retrieve data for user ID: {userId}</p>
                        <button
                            onClick={() => window.location.reload()}
                            className="px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-all font-medium"
                        >
                            <span className="flex items-center gap-2"><ArrowLeft className="h-4 w-4" /> Reload Page</span>
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    <div className={embedded ? "w-full" : "min-h-screen bg-slate-50"}>
                        {/* Main Content */}
                        <div className={embedded ? "w-full" : "flex h-screen bg-slate-50"}>
                            {/* Left Sidebar - Dashboard Navigation (Only if not embedded) */}
                            {!embedded && (
                                <div className="w-64 bg-slate-900 border-r border-slate-800 flex flex-col">
                                    <div className="p-6 border-b border-slate-800">
                                        <div className="flex items-center gap-3">
                                            <div className="h-10 w-10 bg-white rounded-lg flex items-center justify-center p-1">
                                                <img src="/dashboard_logo.png" alt="Polyglots" className="h-full w-full object-contain" />
                                            </div>
                                            <div>
                                                <h1 className="text-lg font-bold text-white">Polyglots</h1>
                                                <p className="text-xs text-slate-400">Staff Portal</p>
                                            </div>
                                        </div>
                                    </div>
                                    {/* Navigation items could be added here if needed for standalone view */}
                                </div>
                            )}

                            {/* Main Content Area */}
                            <div className={embedded ? "w-full" : "flex-1 min-h-screen overflow-y-auto"}>
                                <div className={embedded ? "space-y-6" : "max-w-7xl mx-auto p-6 space-y-6"}>
                                    {/* Back Button */}
                                    {isPageView && window.location.pathname.includes('/staff/profile/') && (
                                        <div className="flex items-center mb-2">
                                            <button
                                                onClick={() => navigate('/staff/approvals')}
                                                className="group flex items-center gap-4 transition-all duration-300"
                                            >
                                                <div className="h-11 w-11 rounded-full border border-slate-200 flex items-center justify-center bg-white shadow-sm group-hover:shadow-md group-hover:border-cyan-300 group-hover:bg-cyan-50 transition-all duration-300">
                                                    <ArrowLeft className="h-5 w-5 text-slate-500 group-hover:text-cyan-600 transition-colors" />
                                                </div>
                                                <span className="text-2xl font-bold text-[#64748b] tracking-tight group-hover:text-cyan-600 transition-colors">Back to Approvals</span>
                                            </button>
                                        </div>
                                    )}

                                    {/* Header Card */}
                                    <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                                        <div className="flex items-center gap-5">
                                            <div className="relative">
                                                <div className="h-16 w-16 rounded-full border-2 border-cyan-500/20 p-0.5">
                                                    <div className="h-full w-full rounded-full bg-slate-100 flex items-center justify-center overflow-hidden border border-white shadow-inner">
                                                        {candidate?.photo_url ? (
                                                            <button
                                                                onClick={() => setShowPhotoExpansion(true)}
                                                                className="h-full w-full overflow-hidden rounded-full shadow-xl hover:scale-105 transition-transform duration-300 group relative"
                                                                title="Click to expand photo"
                                                            >
                                                                <img src={getPhotoUrl(candidate.photo_url)} alt={candidate.name} className="h-full w-full object-cover" />
                                                                <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                                    <Maximize2 className="h-8 w-8 text-white" />
                                                                </div>
                                                            </button>
                                                        ) : (
                                                            <span className="text-xl font-bold text-cyan-600">{candidate?.name?.charAt(0)}</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="space-y-2">
                                                <div>
                                                    <h1 className="text-xl font-bold text-black tracking-tight leading-tight mb-1">{candidate?.name}</h1>
                                                    <div className="flex flex-wrap items-center gap-4 mt-0.5">
                                                        <div className="flex items-center gap-1.5 text-slate-500 font-medium text-xs">
                                                            <Mail className="h-3 w-3 text-cyan-500 shrink-0" />
                                                            <span className="truncate">
                                                                {candidate?.email === '[Hidden]'
                                                                    ? '[Protected]'
                                                                    : (candidate?.role?.includes('partner') && currentUser?.role === 'staff')
                                                                        ? '(Contact Hidden)'
                                                                        : candidate?.email}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-1.5 text-slate-500 font-medium text-xs">
                                                            <Phone className="h-3 w-3 text-cyan-500 shrink-0" />
                                                            <span className="truncate">
                                                                {candidate?.email === '[Hidden]'
                                                                    ? '[Protected]'
                                                                    : (candidate?.role?.includes('partner') && currentUser?.role === 'staff')
                                                                        ? '(Contact Hidden)'
                                                                        : (candidate?.phone || 'Not Provided')}
                                                            </span>
                                                        </div>
                                                        {candidate?.candidate_ref_number && (
                                                            <div className="flex items-center gap-1.5 text-slate-500 font-medium text-xs">
                                                                <span className="bg-slate-100 px-1.5 py-0.5 rounded text-slate-600">ID: {candidate.candidate_ref_number}</span>
                                                            </div>
                                                        )}
                                                        {candidate?.progress_stage && (
                                                            <div className="flex items-center gap-1.5 text-slate-500 font-medium text-xs">
                                                                <span className="bg-indigo-50 px-1.5 py-0.5 rounded text-indigo-600 border border-indigo-100 font-bold uppercase tracking-wider">{candidate.progress_stage}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2 w-full md:w-auto flex-wrap">
                                            {(currentUser?.role === 'admin' || currentUser?.role === 'staff') && (
                                                <div className="flex flex-col md:flex-row items-center gap-2">
                                                    <input
                                                        type="text"
                                                        value={importantUpdate}
                                                        onChange={(e) => setImportantUpdate(e.target.value)}
                                                        placeholder="Post an important update..."
                                                        className="flex-1 min-w-[250px] px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-cyan-500 transition-all outline-none"
                                                    />
                                                    <button
                                                        onClick={handlePostUpdate}
                                                        disabled={isPostingUpdate || !importantUpdate.trim()}
                                                        className="flex-1 md:flex-none flex items-center justify-center gap-1.5 px-4 py-2 bg-indigo-600 text-white font-semibold text-xs rounded-lg hover:bg-indigo-700 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        <Plus className="h-3.5 w-3.5" /> Post Update
                                                    </button>
                                                </div>
                                            )}

                                            {(currentUser?.role === 'admin' || currentUser?.role === 'staff') && (
                                                <>
                                                    {currentUser?.role === 'admin' && (
                                                        <button
                                                            onClick={() => handleImpersonate(candidate.id, candidate.role)}
                                                            className="flex-1 md:flex-none flex items-center justify-center gap-1.5 px-4 py-2 bg-emerald-600 text-white font-semibold text-xs rounded-lg hover:bg-emerald-700 transition-all shadow-sm"
                                                            title="Impersonate this user"
                                                        >
                                                            <UserCheck className="h-3.5 w-3.5" /> Impersonate
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => {
                                                            const { generateProfilePDF } = require('../utils/generateProfilePDF');
                                                            generateProfilePDF(candidate, candidate, false, 'en');
                                                        }}
                                                        className="flex-1 md:flex-none flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-700 text-white font-semibold text-xs rounded-lg hover:bg-blue-800 transition-all shadow-sm"
                                                        title="Download EU Resume (English)"
                                                    >
                                                        <Download className="h-3.5 w-3.5" /> 🇪🇺 EU Resume
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            const { generateProfilePDF } = require('../utils/generateProfilePDF');
                                                            generateProfilePDF(candidate, candidate, false, 'de');
                                                        }}
                                                        className="flex-1 md:flex-none flex items-center justify-center gap-1.5 px-3 py-2 bg-slate-700 text-white font-semibold text-xs rounded-lg hover:bg-slate-800 transition-all shadow-sm"
                                                        title="Download EU Resume (German)"
                                                    >
                                                        <Download className="h-3.5 w-3.5" /> 🇩🇪 Lebenslauf
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            const { generateProfilePDF } = require('../utils/generateProfilePDF');
                                                            const dashboardData = {
                                                                employment_history: candidate.employment_history,
                                                                education_history: candidate.education_history,
                                                                language_certificates: candidate.language_certificates,
                                                                certificates: candidate.certificates
                                                            };
                                                            generateProfilePDF(candidate, dashboardData, true);
                                                        }}
                                                        className="flex-1 md:flex-none flex items-center justify-center gap-1.5 px-4 py-2 bg-amber-50 text-amber-700 font-semibold text-xs rounded-lg hover:bg-amber-100 transition-all shadow-sm border border-amber-200"
                                                        title={t('adminDashboard.previewEmployer', 'Preview what Employers see')}
                                                    >
                                                        <Shield className="h-3.5 w-3.5" /> {t('adminDashboard.maskedCV', 'Masked CV')}
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    {/* Tabs Navigation */}
                                    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                                        <div className="border-b border-slate-200 px-6 overflow-x-auto">
                                            <div className="flex gap-6 min-w-max">
                                                {tabs.map(tab => (
                                                    <button
                                                        key={tab.id}
                                                        onClick={() => setActiveTab(tab.id)}
                                                        className={`py-4 text-sm font-medium transition-all ${activeTab === tab.id
                                                            ? 'text-cyan-600 border-b-2 border-cyan-600 font-semibold'
                                                            : 'text-slate-500 hover:text-slate-700'
                                                            }`}
                                                    >
                                                        {tab.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="p-6">
                                            {renderTabContent()}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Edit Modals */}
                    <EditPersonalDetailsModal
                        isOpen={editingSection === 'personal'}
                        onClose={() => setEditingSection(null)}
                        userData={candidate}
                        onSave={handleSectionSave}
                    />

                    <EditAddressModal
                        isOpen={editingSection === 'address'}
                        onClose={() => setEditingSection(null)}
                        userData={candidate}
                        onSave={handleSectionSave}
                    />

                    <EditNationalityModal
                        isOpen={editingSection === 'nationality'}
                        onClose={() => setEditingSection(null)}
                        userData={candidate}
                        onSave={handleSectionSave}
                    />

                    <EditLanguageModal
                        isOpen={editingSection === 'language'}
                        onClose={() => {
                            setEditingSection(null);
                            setEditingLanguageIndex(null);
                        }}
                        languageData={editingLanguageIndex !== null ? (candidate?.language_certificates || [])[editingLanguageIndex] : null}
                        onSave={handleLanguageSave}
                        isAddMode={editingLanguageIndex === null}
                    />

                    <AddEmploymentModal
                        isOpen={showEmploymentModal}
                        onClose={() => {
                            setShowEmploymentModal(false);
                            setEditingEmploymentIndex(null);
                        }}
                        employmentData={editingEmploymentIndex !== null ? (candidate?.employment_history || [])[editingEmploymentIndex] : null}
                        onSave={handleEmploymentSave}
                        isAddMode={editingEmploymentIndex === null}
                    />

                    <AddEducationModal
                        isOpen={showEducationModal}
                        onClose={() => {
                            setShowEducationModal(false);
                            setEditingEducationIndex(null);
                        }}
                        educationData={editingEducationIndex !== null ? (candidate?.education_history || [])[editingEducationIndex] : null}
                        onSave={handleEducationSave}
                        isAddMode={editingEducationIndex === null}
                    />

                    <AddSkillModal
                        isOpen={showSkillModal}
                        onClose={() => setShowSkillModal(false)}
                        onSave={handleAddSkill}
                    />

                    <AddCertificateModal
                        isOpen={showCertificateModal}
                        onClose={() => {
                            setShowCertificateModal(false);
                            setEditingCertificateIndex(null);
                        }}
                        certificateData={editingCertificateIndex !== null ? (candidate?.certificates || [])[editingCertificateIndex] : null}
                        onSave={handleCertificateSave}
                        isAddMode={editingCertificateIndex === null}
                    />

                    <AddDocumentModal
                        isOpen={showDocumentModal}
                        onClose={() => {
                            setShowDocumentModal(false);
                            setEditingDocumentIndex(null);
                        }}
                        documentData={editingDocumentIndex !== null ? (candidate?.documents || [])[editingDocumentIndex] : null}
                        onSave={handleDocumentSave}
                        isAddMode={editingDocumentIndex === null}
                    />

                    <AddNoteModal
                        isOpen={showNoteModal}
                        onClose={() => {
                            setShowNoteModal(false);
                            setEditingNoteIndex(null);
                        }}
                        noteData={editingNoteIndex !== null ? (candidate?.notes || [])[editingNoteIndex] : null}
                        onSave={handleNoteSave}
                        isAddMode={editingNoteIndex === null}
                    />

                    {/* Photo Expansion Modal */}
                    {showPhotoExpansion && (
                        <div
                            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4 animate-fadeIn"
                            onClick={() => setShowPhotoExpansion(false)}
                        >
                            <button
                                onClick={() => setShowPhotoExpansion(false)}
                                className="absolute top-6 right-6 p-2 text-white/50 hover:text-white transition-colors"
                            >
                                <X className="h-8 w-8" />
                            </button>
                            <div className="max-w-[90vw] max-h-[90vh] relative animate-scaleIn" onClick={e => e.stopPropagation()}>
                                <img
                                    src={getPhotoUrl(candidate.photo_url)}
                                    alt={candidate.name}
                                    className="max-w-full max-h-[85vh] rounded-2xl shadow-2xl border-4 border-white/10"
                                />
                                <div className="mt-4 text-center">
                                    <p className="text-white font-bold text-lg">{candidate.name}</p>
                                    <p className="text-white/60 text-sm uppercase tracking-widest">{candidate.occupation || 'Candidate'}</p>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )
            }
        </div >
    );
};

export default CandidateProfileView;
